import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  ArrowLeft,
  Users,
  UserPlus,
  Eye,
  EyeOff,
  Key,
  ToggleLeft,
  ToggleRight,
  Shield,
  ShieldCheck,
  Crown,
  Activity,
  Calendar,
  Package
} from 'lucide-react';

interface User {
  id: number;
  nome: string;
  username: string;
  email: string;
  ruolo: 'admin' | 'manager' | 'produzione' | 'banconista';
  attivo: boolean;
  punto_vendita: {
    id: number;
    nome: string;
  };
  created_at: string;
  updated_at: string;
  statistics: {
    total_attivita: number;
    ultimo_accesso: string | null;
    produzioni_create: number;
  };
}

interface TenantUsersData {
  success: boolean;
  tenant: {
    id: number;
    nome: string;
  };
  utenti: User[];
  stats: {
    totale: number;
    attivi: number;
    admin: number;
    manager: number;
    operatori: number;
  };
}

const Page = styled.div`
  min-height: 100vh;
  background: #f8fafc;
`;

const TopBar = styled.div`
  background: white;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
`;

const TopBarInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 1.5rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const TopBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  padding: 0;
  font-size: 0.9375rem;
  color: #64748b;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #1e293b;
  }

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const Divider = styled.div`
  height: 1.5rem;
  width: 1px;
  background: #e5e7eb;
`;

const Title = styled.h1`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;

  svg {
    width: 1.5rem;
    height: 1.5rem;
    color: #3b82f6;
  }
`;

const Subtitle = styled.p`
  margin: 0.25rem 0 0;
  color: #64748b;
`;

const PrimaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.9375rem;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background: #2563eb;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const Content = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 2rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  padding: 1.25rem;

  svg {
    width: 1.75rem;
    height: 1.75rem;
  }
`;

const StatValue = styled.p`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
`;

const StatLabel = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: #64748b;
`;

const ActiveDot = styled.div`
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 50%;
  background: #22c55e;
  flex-shrink: 0;
`;

const Panel = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  overflow: hidden;
`;

const PanelHeader = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;

  h2 {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 600;
    color: #1e293b;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  thead {
    background: #f8fafc;
  }

  th {
    padding: 0.75rem 1.5rem;
    text-align: left;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #64748b;
  }

  td {
    padding: 1rem 1.5rem;
    border-top: 1px solid #e5e7eb;
    white-space: nowrap;
    font-size: 0.875rem;
    color: #1e293b;
    vertical-align: top;
  }

  tr.inactive {
    background: #f8fafc;
    opacity: 0.75;
  }
`;

const UserName = styled.div`
  font-weight: 500;
  color: #1e293b;
`;

const UserEmail = styled.div`
  color: #64748b;
`;

const UserHandle = styled.div`
  font-size: 0.75rem;
  color: #94a3b8;
`;

const RoleCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-transform: capitalize;

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const StatLine = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  color: #475569;

  svg {
    width: 0.75rem;
    height: 0.75rem;
  }

  &.muted {
    font-size: 0.75rem;
    color: #64748b;
  }

  & + & {
    margin-top: 0.25rem;
  }
`;

const StatusToggle = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.625rem;
  border: none;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
  background: ${({ $active }) => ($active ? '#dcfce7' : '#fee2e2')};
  color: ${({ $active }) => ($active ? '#166534' : '#991b1b')};

  &:hover {
    background: ${({ $active }) => ($active ? '#bbf7d0' : '#fecaca')};
  }

  svg {
    width: 0.875rem;
    height: 0.875rem;
  }
`;

const LinkButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  background: none;
  border: none;
  padding: 0;
  font-size: 0.875rem;
  font-weight: 500;
  color: #3b82f6;
  cursor: pointer;

  &:hover {
    color: #1d4ed8;
  }

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
  padding: 1rem;
`;

const ModalBox = styled.div`
  background: white;
  border-radius: 0.5rem;
  padding: 1.5rem;
  width: 100%;
  max-width: 28rem;

  h3 {
    margin: 0 0 1rem;
    font-size: 1.125rem;
    font-weight: 600;
    color: #1e293b;
  }

  p {
    margin: 0 0 1rem;
    color: #475569;
  }
`;

const Field = styled.div`
  margin-bottom: 1rem;

  label {
    display: block;
    margin-bottom: 0.375rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: #334155;
  }
`;

const TextInput = styled.input`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.375rem;
  font-size: 0.9375rem;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.25);
  }
`;

const SelectInput = styled.select`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.375rem;
  font-size: 0.9375rem;
  background: white;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.25);
  }
`;

const PasswordWrapper = styled.div`
  position: relative;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 0;
  color: #94a3b8;
  cursor: pointer;

  &:hover {
    color: #475569;
  }

  svg {
    width: 1rem;
    height: 1rem;
    display: block;
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
`;

const SecondaryButton = styled.button`
  padding: 0.5rem 1rem;
  background: white;
  color: #475569;
  border: 1px solid #cbd5e1;
  border-radius: 0.375rem;
  font-size: 0.9375rem;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: #f8fafc;
  }
`;

const CenteredScreen = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
`;

const Spinner = styled.div`
  width: 3rem;
  height: 3rem;
  margin: 0 auto 1rem;
  border: 3px solid #e5e7eb;
  border-bottom-color: #3b82f6;
  border-radius: 50%;
  animation: tenant-users-spin 1s linear infinite;

  @keyframes tenant-users-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ErrorPanel = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 0.5rem;
  padding: 1.5rem;

  h2 {
    margin: 0 0 0.5rem;
    font-size: 1.125rem;
    font-weight: 600;
    color: #991b1b;
  }

  p {
    margin: 0;
    color: #dc2626;
  }
`;

const ErrorBack = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  background: none;
  border: none;
  padding: 0;
  color: #b91c1c;
  cursor: pointer;

  &:hover {
    color: #7f1d1d;
  }

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

export default function TenantUsersPage() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<TenantUsersData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [passwordResetUser, setPasswordResetUser] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Form per nuovo utente
  const [newUser, setNewUser] = useState({
    nome: '',
    email: '',
    password: '',
    ruolo: 'banconista' as const,
    username: ''
  });

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch solo al cambio tenant; loadUsers è ricreata ad ogni render
  }, [tenantId]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : ''}/api/superadmin/tenants/${tenantId}/users`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('superadmin_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Errore HTTP: ${response.status}`);
      }

      const result: TenantUsersData = await response.json();
      setData(result);
    } catch (err: any) {
      setError(`Errore caricamento utenti: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!passwordResetUser || !newPassword) return;

    try {
      const response = await fetch(`${process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : ''}/api/superadmin/tenants/${tenantId}/users/${passwordResetUser.id}/reset-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('superadmin_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ newPassword })
      });

      if (!response.ok) {
        throw new Error(`Errore HTTP: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        alert(`Password reimpostata per ${passwordResetUser.nome}.`);
        setPasswordResetUser(null);
        setNewPassword('');
      } else {
        throw new Error(result.error || 'Errore sconosciuto');
      }
    } catch (err: any) {
      setError(`Errore reset password: ${err.message}`);
    }
  };

  const handleToggleUserStatus = async (user: User) => {
    try {
      const response = await fetch(`${process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : ''}/api/superadmin/tenants/${tenantId}/users/${user.id}/toggle-status`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('superadmin_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Errore HTTP: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        alert(result.message);
        await loadUsers(); // Ricarica la lista utenti
      } else {
        throw new Error(result.error || 'Errore sconosciuto');
      }
    } catch (err: any) {
      setError(`Errore cambio stato: ${err.message}`);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const response = await fetch(`${process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : ''}/api/superadmin/tenants/${tenantId}/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('superadmin_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newUser)
      });

      if (!response.ok) {
        throw new Error(`Errore HTTP: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        alert(result.message);
        setShowCreateForm(false);
        setNewUser({ nome: '', email: '', password: '', ruolo: 'banconista', username: '' });
        await loadUsers(); // Ricarica la lista utenti
      } else {
        throw new Error(result.error || 'Errore sconosciuto');
      }
    } catch (err: any) {
      setError(`Errore creazione utente: ${err.message}`);
    }
  };

  const getRoleIcon = (ruolo: string) => {
    switch (ruolo) {
      case 'admin': return <Crown color="#eab308" />;
      case 'manager': return <ShieldCheck color="#3b82f6" />;
      case 'produzione': return <Shield color="#f97316" />;
      case 'banconista': return <Shield color="#64748b" />;
      default: return <Shield color="#64748b" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <CenteredScreen>
        <div style={{ textAlign: 'center' }}>
          <Spinner />
          <p style={{ color: '#475569' }}>Caricamento utenti...</p>
        </div>
      </CenteredScreen>
    );
  }

  if (error) {
    return (
      <Page>
        <Content>
          <ErrorPanel>
            <h2>Errore</h2>
            <p>{error}</p>
            <ErrorBack onClick={() => navigate('/superadmin')}>
              <ArrowLeft />
              Torna alla Dashboard
            </ErrorBack>
          </ErrorPanel>
        </Content>
      </Page>
    );
  }

  if (!data) return null;

  return (
    <Page>
      <TopBar>
        <TopBarInner>
          <TopBarLeft>
            <BackButton onClick={() => navigate('/superadmin')}>
              <ArrowLeft />
              Dashboard
            </BackButton>
            <Divider />
            <div>
              <Title>
                <Users />
                Utenti - {data.tenant.nome}
              </Title>
              <Subtitle>Gestione utenti del tenant {data.tenant.id}</Subtitle>
            </div>
          </TopBarLeft>
          <PrimaryButton onClick={() => setShowCreateForm(true)}>
            <UserPlus />
            Nuovo Utente
          </PrimaryButton>
        </TopBarInner>
      </TopBar>

      <Content>
        {/* Statistiche */}
        <StatsGrid>
          <StatCard>
            <Users color="#3b82f6" />
            <div>
              <StatValue>{data.stats.totale}</StatValue>
              <StatLabel>Totale</StatLabel>
            </div>
          </StatCard>
          <StatCard>
            <ActiveDot />
            <div>
              <StatValue>{data.stats.attivi}</StatValue>
              <StatLabel>Attivi</StatLabel>
            </div>
          </StatCard>
          <StatCard>
            <Crown color="#eab308" />
            <div>
              <StatValue>{data.stats.admin}</StatValue>
              <StatLabel>Admin</StatLabel>
            </div>
          </StatCard>
          <StatCard>
            <ShieldCheck color="#3b82f6" />
            <div>
              <StatValue>{data.stats.manager}</StatValue>
              <StatLabel>Manager</StatLabel>
            </div>
          </StatCard>
          <StatCard>
            <Shield color="#64748b" />
            <div>
              <StatValue>{data.stats.operatori}</StatValue>
              <StatLabel>Operatori</StatLabel>
            </div>
          </StatCard>
        </StatsGrid>

        {/* Lista Utenti */}
        <Panel>
          <PanelHeader>
            <h2>Lista Utenti</h2>
          </PanelHeader>

          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <th>Utente</th>
                  <th>Ruolo</th>
                  <th>Punto Vendita</th>
                  <th>Statistiche</th>
                  <th>Stato</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {data.utenti.map((user) => (
                  <tr key={user.id} className={!user.attivo ? 'inactive' : ''}>
                    <td>
                      <UserName>{user.nome}</UserName>
                      <UserEmail>{user.email}</UserEmail>
                      <UserHandle>@{user.username}</UserHandle>
                    </td>
                    <td>
                      <RoleCell>
                        {getRoleIcon(user.ruolo)}
                        <span>{user.ruolo}</span>
                      </RoleCell>
                    </td>
                    <td>{user.punto_vendita?.nome || 'Non assegnato'}</td>
                    <td>
                      <StatLine>
                        <Activity />
                        {user.statistics.total_attivita} attività
                      </StatLine>
                      <StatLine>
                        <Package />
                        {user.statistics.produzioni_create} produzioni
                      </StatLine>
                      {user.statistics.ultimo_accesso && (
                        <StatLine className="muted">
                          <Calendar />
                          {formatDate(user.statistics.ultimo_accesso)}
                        </StatLine>
                      )}
                    </td>
                    <td>
                      <StatusToggle $active={user.attivo} onClick={() => handleToggleUserStatus(user)}>
                        {user.attivo ? <ToggleRight /> : <ToggleLeft />}
                        {user.attivo ? 'Attivo' : 'Disattivo'}
                      </StatusToggle>
                    </td>
                    <td>
                      <LinkButton onClick={() => setPasswordResetUser(user)}>
                        <Key />
                        Reset Password
                      </LinkButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        </Panel>
      </Content>

      {/* Modal Reset Password */}
      {passwordResetUser && (
        <ModalOverlay>
          <ModalBox>
            <h3>Reset Password</h3>
            <p>
              Imposta una nuova password per <strong>{passwordResetUser.nome}</strong> ({passwordResetUser.username})
            </p>
            <Field>
              <label>Nuova Password</label>
              <PasswordWrapper>
                <TextInput
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Inserisci nuova password"
                  minLength={6}
                />
                <PasswordToggle type="button" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff /> : <Eye />}
                </PasswordToggle>
              </PasswordWrapper>
            </Field>
            <ModalActions>
              <SecondaryButton
                onClick={() => {
                  setPasswordResetUser(null);
                  setNewPassword('');
                  setShowPassword(false);
                }}
              >
                Annulla
              </SecondaryButton>
              <PrimaryButton onClick={handleResetPassword} disabled={newPassword.length < 6}>
                Reimposta Password
              </PrimaryButton>
            </ModalActions>
          </ModalBox>
        </ModalOverlay>
      )}

      {/* Modal Crea Utente */}
      {showCreateForm && (
        <ModalOverlay>
          <ModalBox>
            <h3>Crea Nuovo Utente</h3>
            <form onSubmit={handleCreateUser}>
              <Field>
                <label>Nome *</label>
                <TextInput
                  type="text"
                  value={newUser.nome}
                  onChange={(e) => setNewUser({ ...newUser, nome: e.target.value })}
                  required
                />
              </Field>
              <Field>
                <label>Email *</label>
                <TextInput
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  required
                />
              </Field>
              <Field>
                <label>Username (opzionale)</label>
                <TextInput
                  type="text"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  placeholder="Se vuoto, verrà generato dall'email"
                />
              </Field>
              <Field>
                <label>Password *</label>
                <TextInput
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  minLength={6}
                  required
                />
              </Field>
              <Field>
                <label>Ruolo *</label>
                <SelectInput
                  value={newUser.ruolo}
                  onChange={(e) => setNewUser({ ...newUser, ruolo: e.target.value as any })}
                  required
                >
                  <option value="produzione">Produzione</option>
                  <option value="banconista">Banconista</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </SelectInput>
              </Field>
              <ModalActions>
                <SecondaryButton
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    setNewUser({ nome: '', email: '', password: '', ruolo: 'banconista', username: '' });
                  }}
                >
                  Annulla
                </SecondaryButton>
                <PrimaryButton type="submit">Crea Utente</PrimaryButton>
              </ModalActions>
            </form>
          </ModalBox>
        </ModalOverlay>
      )}
    </Page>
  );
}
